import { Button } from "@components/button";
import Dialog from "@components/dialog/Dialog";
import { Label } from "@components/inputs/field";
import { IInputOnChangeEvent } from "@components/inputs/input";
import { IDocumentEntity } from "@odata/GeneratedEntityTypes";
import {
    AmountRow,
    InputColumn,
    LabelColumn,
    NumericInputStyled,
    TableStyled,
    ValueColumn,
    ValuePadding
} from "@pages/banks/bankTransactions/BankTransactions.styles";
import i18next from "i18next";
import React, { ReactElement } from "react";

import { BasicInputSizes, TextAlign, ValidationErrorType } from "../../../enums";
import { IValidationError } from "../../../model/Validator.types";
import TestIds from "../../../testIds";
import CurrencyType, { formatCurrency } from "../../../types/Currency";
import NumberType, { currencyScaleFormatter, IParserArgs } from "../../../types/Number";
import ConfirmationButtons from "../../../views/table/ConfirmationButtons";

export interface IEditableAmountDialogData extends Required<Pick<IDocumentEntity, "TransactionAmount" | "TransactionCurrency" | "NumberOurs">>, Pick<IDocumentEntity, "ExchangeRatePerUnit" | "Amount" | "Currency"> {
    TotalAmount: number;
}

interface IEditableAmountDialogProps {
    data: IEditableAmountDialogData;
    tNamespace?: string;

    isReadOnly?: boolean;
    isBusy?: boolean;
    max?: number;

    onClose: () => void;
    onConfirm: (amount: number) => void;
}

interface IState {
    currentAmount: number;
    error?: IValidationError;
}

export class EditableAmountDialog extends React.Component<IEditableAmountDialogProps, IState> {

    constructor(props: IEditableAmountDialogProps) {
        super(props);

        this.state = {
            currentAmount: props.data.TransactionAmount
        };
    }

    componentDidUpdate() {
        // todo: check updated props?
    }

    get exchangeRate(): number {
        return this.props.data?.ExchangeRatePerUnit ?? 1;
    }

    get scale(): number {
        return this.props.data?.TransactionCurrency?.MinorUnit ?? 2;
    }

    get tNamespace(): string {
        return this.props.tNamespace ?? "Components:SelectionTableView";
    }

    canConfirm = (): boolean => {
        return !!this.state.currentAmount && !this.state.error;
    };

    clearAmountDialogFields = (): void => {
        this.setState({
            currentAmount: 0,
            error: null
        });
    };

    handleCancelAmountDialog = (): void => {
        this.props.onClose();
    };

    handleConfirmAmountDialog = (): void => {
        const { currentAmount } = this.state;

        const error = this.getValidationError(currentAmount);
        if (error) {
            return;
        }

        this.props.onConfirm(currentAmount);
    };

    getValidationError = (value: number): IValidationError => {
        const max = this.props.max ?? this.props.data.TotalAmount;

        const getValidationError = (key: string, data: Record<string, string | number> = {}): IValidationError => ({
            message: i18next.t(`Common:Validation.${key}`, { ...data }),
            errorType: ValidationErrorType.Field
        });

        switch (true) {
            case !value:
                return getValidationError("Required");
            case isNaN(value):
                return getValidationError("NotANumber");
            case value <= 0:
                return getValidationError("PositiveNumber");
            case value > max:
                return getValidationError("BigNumber", { max });
        }

        return null;
    };

    handleAmountChange = (e: IInputOnChangeEvent<number>): void => {
        this.setState({
            currentAmount: e.value as number,
            error: null
        });
    };

    handleAmountBlur = (): void => {
        const { currentAmount } = this.state;
        const error = this.getValidationError(currentAmount);

        if (error) {
            this.setState({ error });
        }
    }

    renderContent = (): ReactElement => {
        const { isReadOnly, data } = this.props;
        const { TotalAmount, TransactionCurrency, Currency } = data;
        const { exchangeRate } = this;
        const { currentAmount, error } = this.state;

        const trCurrencyCode = TransactionCurrency?.Code;
        const currencyCode = Currency?.Code;

        const totalAmountFormatted = formatCurrency(TotalAmount, trCurrencyCode);
        const secondColPadding = isReadOnly ? ValuePadding.None : ValuePadding.Input;

        const hasSameCurrencies = trCurrencyCode === currencyCode;
        return (
                <TableStyled>
                    <tbody>
                    {!hasSameCurrencies && (
                            <AmountRow>
                                <LabelColumn></LabelColumn>
                                <ValueColumn
                                        padding={secondColPadding}>{i18next.t(`${this.tNamespace}.Amount`)}</ValueColumn>
                                <ValueColumn padding={ValuePadding.Gap}>{i18next.t(`${this.tNamespace}.ExchangeRate`)}</ValueColumn>
                                <ValueColumn>{i18next.t(`${this.tNamespace}.Accounted`)}</ValueColumn>
                            </AmountRow>
                    )}
                    <AmountRow>
                        <LabelColumn>
                            <Label>{i18next.t(`${this.tNamespace}.Total`)}</Label>
                        </LabelColumn>
                        <ValueColumn padding={secondColPadding}>{totalAmountFormatted}</ValueColumn>
                        {!hasSameCurrencies && (<>
                            <ValueColumn padding={ValuePadding.Gap}>{exchangeRate}</ValueColumn>
                            <ValueColumn>{formatCurrency(TotalAmount * exchangeRate, currencyCode)}</ValueColumn>
                        </>)}
                    </AmountRow>
                    <AmountRow>
                        <LabelColumn>
                            <Label isRequired>{i18next.t(`${this.tNamespace}.Paid`)}</Label>
                        </LabelColumn>
                        <InputColumn isLast>
                            <NumericInputStyled
                                    onChange={this.handleAmountChange}
                                    onBlur={this.handleAmountBlur}
                                    isReadOnly={isReadOnly}
                                    unit={CurrencyType.getCurrencyUnit(trCurrencyCode)}
                                    width={BasicInputSizes.S}
                                    textAlign={TextAlign.Right}
                                    parser={this.amountParser}
                                    formatter={this.amountFormatter}
                                    error={error}
                                    value={currentAmount}/>
                        </InputColumn>
                        {!hasSameCurrencies && (<>
                            <ValueColumn padding={ValuePadding.Gap}>{exchangeRate}</ValueColumn>
                            <ValueColumn>{formatCurrency(TotalAmount * exchangeRate, currencyCode)}</ValueColumn>
                        </>)}
                    </AmountRow>
                    </tbody>
                </TableStyled>
        );
    };

    amountParser = (value: string, strict?: boolean, args?: IParserArgs): number => {
        return NumberType.parse(value, strict, {
            ...args,
            maximumFractionDigits: this.scale
        });
    };

    amountFormatter = (value: number): string => {
        return currencyScaleFormatter(value);
    };

    getTitle(): string {
        return i18next.t(`${this.tNamespace}.PaymentOf`, { numberOurs: this.props.data?.NumberOurs });
    }

    getFooterButtons = (): ReactElement => {
        if (this.props.isReadOnly) {
            return (
                    <Button onClick={this.handleCancelAmountDialog}>
                        {i18next.t("Common:General.Close")}
                    </Button>
            );
        }

        return <ConfirmationButtons isDisabled={!this.canConfirm()}
                                    onCancel={this.handleCancelAmountDialog}
                                    onConfirm={this.handleConfirmAmountDialog}
                                    useWrapper={false}/>;
    };

    render() {
        return <Dialog
                busy={this.props.isBusy}
                onConfirm={this.handleConfirmAmountDialog}
                onClose={this.handleCancelAmountDialog}
                footer={this.getFooterButtons()}
                title={this.getTitle()}
                testid={TestIds.PencilDialog}>
            {!this.props.isBusy && this.renderContent()}
        </Dialog>;
    }
}